import { useQuery } from '@tanstack/react-query';
import { Alert, Spin } from 'antd';
import queryString from 'query-string';
import { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { resetProps, updateProps } from '../../contextRedux/Provider/ProviderModel';
import { setProviders } from '../../contextRedux/Provider/ProviderStore';
import { setProviderGroups } from '../../contextRedux/ProviderGroup/ProviderGroupStore';
import { AnimateButton } from '../../customComponents/button/CustomButton';
import { ControllerResponse } from '../../DTOS/Controller/ControllerResponse';
import ProviderService from '../../services/provider/ProviderService';
import ProviderGroupService from '../../services/providerGroup/ProviderGroupService';
import { RootState } from '../../store';
import ProviderModal from './ProviderModal/ProviderModal';
import ProviderTable from './ProviderTable/ProviderTable';

interface ProviderProps {}

const Provider: FC<ProviderProps> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const searchTerm = useSelector((state: RootState) => state.models?.provider.props.searchTerm);
  const skip = useSelector((state: RootState) => state.models?.provider.props.skip);
  const take = useSelector((state: RootState) => state.models?.provider.props.take);
  const refresh = useSelector((state: RootState) => state.models?.provider.props.refresh);

  const [isParamsInitialized, setIsParamsInitialized] = useState<boolean>(false);

  const previousParamsRef = useRef({ term: '', page: '', pageSize: '' });

  useEffect(() => {
    const queryParams = queryString.parse(location.search);

    const termInUrl = (queryParams.term as string) || '';
    const pageInUrl = Number(queryParams.page) || 1;
    const pageSizeInUrl = Number(queryParams.pageSize) || take;

    const calculatedSkip = (pageInUrl - 1) * pageSizeInUrl;

    dispatch(updateProps({ field: 'searchTerm', value: termInUrl }));
    dispatch(updateProps({ field: 'skip', value: calculatedSkip }));
    dispatch(updateProps({ field: 'take', value: pageSizeInUrl }));

    previousParamsRef.current = { term: termInUrl, page: pageInUrl.toString(), pageSize: pageSizeInUrl.toString() };
    setIsParamsInitialized(true);
  }, [dispatch, location.search, take]);

  useEffect(() => {
    if (isParamsInitialized) {
      const queryParams = queryString.parse(location.search);
      const currentPage = Math.floor(skip / take) + 1;

      if (
        searchTerm !== previousParamsRef.current.term ||
        currentPage.toString() !== previousParamsRef.current.page ||
        take.toString() !== previousParamsRef.current.pageSize
      ) {
        const params = {
          ...queryParams,
          term: searchTerm || undefined,
          page: currentPage > 1 ? currentPage.toString() : undefined,
          pageSize: take !== 10 ? take.toString() : undefined,
        };

        const newSearch = queryString.stringify(params);
        navigate(`${location.pathname}?${newSearch}`, { replace: true });

        previousParamsRef.current = {
          term: searchTerm || '',
          page: currentPage.toString(),
          pageSize: take.toString(),
        };
      }
    }
  }, [searchTerm, skip, take, isParamsInitialized, navigate, location.pathname, location.search]);

  const { data: _providerGroups, isFetching: isProviderGroupsLoading } = useQuery({
    queryKey: ['providerGroups'],
    queryFn: () =>
      ProviderGroupService.search({
        isActive: true,
      }).then((data) => {
        dispatch(setProviderGroups(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const {
    data: _dates,
    isFetching: isProvidersLoading,
    isError: isErrorOnFetchProviders,
    error: providersError,
    refetch,
  } = useQuery({
    queryKey: ['providers', searchTerm, skip, take, refresh],
    queryFn: () =>
      ProviderService.search({
        IsProviderGroupIncluded: true,
        termen: searchTerm || undefined,
        skip,
        take,
      }).then((data: ControllerResponse<any>) => {
        dispatch(updateProps({ field: 'totalItems', value: data?.totalItems || 0 }));
        dispatch(setProviders(data?.response));
        return data?.response;
      }),
    enabled: isParamsInitialized,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (isParamsInitialized && searchTerm === '') {
      refetch();
    }
  }, [searchTerm, isParamsInitialized, refetch]);

  const handleReset = () => {
    dispatch(
      resetProps({
        isModalVisible: true,
        refresh: true,
        searchTerm,
        skip,
        take,
      })
    );
  };

  if (isProviderGroupsLoading) {
    return <Spin size='large' className='spinner' />;
  }

  if (isProvidersLoading) {
    return <Spin size='large' className='spinner' />;
  }

  if (isErrorOnFetchProviders) {
    return <Alert message={providersError.message} type='error' />;
  }

  return (
    <>
      <h1 style={{ marginBottom: 50 }}>Furnizori</h1>
      <AnimateButton onClick={() => handleReset()}>Adauga</AnimateButton>
      <ProviderModal />
      <ProviderTable />
    </>
  );
};

export default Provider;
